import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { HudPillar } from './HudPillar'
import { pillarIcons } from './pillarIcons'
import styles from './HomePillars.module.css'

type Pillar = { title: string; body: ReactNode }

// T3 §1.2 pillars, in T1's H2 order. Competitive-play pillars (1–3) render first
// and heaviest; 4–7 are the calmer secondary grid. Coaching appears only as a
// one-liner inside pillar 6, never its own pillar (honesty rail).
const PILLARS: Pillar[] = [
  {
    title: 'Run every tournament format',
    body: 'Americano, Mexicano, King of the Court, round-robin, elimination, two-stage — open registration, generate the draw, run the schedule across your courts, and finalize. No spreadsheets, no hand-drawn brackets.',
  },
  {
    title: 'Automatic TrueSkill player ratings',
    body: "Every finalized match updates each player's TrueSkill rating, so levels stay honest and matchups stay fair. Your club leaderboard ranks members by that same number — no manual grading.",
  },
  {
    title: 'Ladders & leagues that keep members playing',
    body: 'Give members a club ladder with a clear challenge flow — propose, accept, play — and run season-long Americano or Mexicano leagues with a cumulative points table. Always something to climb between tournaments.',
  },
  {
    title: 'Clinics & programs',
    body: 'Schedule coached clinics and programs, take enrollments per session, manage rosters and mark attendance — for adults and juniors alike.',
  },
  {
    title: 'Court bookings & scheduling',
    body: 'Organize your court inventory and daily schedule on one board — single or multi-court bookings, private-lesson slots, and open games players can join.',
  },
  {
    title: 'Members & community in one place',
    body: 'Keep followers and paying members clearly separated, offer membership plans with real perks, and give every player a profile they own. Coaches get their own view of the sessions they teach, with rosters and attendance.',
  },
  {
    title: 'Marketing that fills your courts',
    body: "Turn a quiet week into a full one. The Racket Center's AI drafts posts and campaigns from your club's real events and results — and nothing publishes until you approve it.",
  },
]

function pillarAt(index: number): ReactNode {
  const pillar = PILLARS[index]
  return (
    <HudPillar
      key={pillar.title}
      num={String(index + 1).padStart(2, '0')}
      icon={pillarIcons[index]}
      title={pillar.title}
      body={pillar.body}
    />
  )
}

export function HomePillars() {
  return (
    <section className={styles.pillars} aria-label="What your club runs" data-track-section>
      {/* On a phone all seven cards fold as ONE deck (docs/ux/2026-09-24-home-rolling-ball.md
          §9.2). The link stays outside, so the pile never slides over it. */}
      <div className={styles.cards} data-deck>
        <div className={styles.primary} data-pillars>
          {[0, 1, 2].map(pillarAt)}
        </div>
        <div className={styles.secondary} data-pillars>
          {[3, 4, 5, 6].map(pillarAt)}
        </div>
      </div>
      <Link className={styles.linkOut} to="/for-clubs">
        See how it works for clubs <span aria-hidden="true">→</span>
      </Link>
    </section>
  )
}
